import { apiClient } from './client';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  /** Effective feature access (catalog ids). Computed server-side from role defaults + overrides. */
  access?: string[];
  twoFactorEnabled?: boolean;
  twoFactorRequired?: boolean;
}

export interface LoginResponse {
  user: User;
  csrfToken: string;
  requiresTwoFactorSetup?: boolean;
}

export interface TwoFactorChallengeResponse {
  requiresTwoFactor: true;
  challengeToken: string;
  expiresAt: string;
}

export type LoginResult = LoginResponse | TwoFactorChallengeResponse;

export function isTwoFactorChallenge(
  response: LoginResult
): response is TwoFactorChallengeResponse {
  return 'requiresTwoFactor' in response && response.requiresTwoFactor === true;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await apiClient.post<LoginResult>('/auth/login', { email, password });

  if (!isTwoFactorChallenge(response)) {
    // Store CSRF token for subsequent requests
    apiClient.setCsrfToken(response.csrfToken);
  }

  return response;
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } finally {
    apiClient.setCsrfToken(null);
  }
}

export async function refreshSession(): Promise<boolean> {
  try {
    await apiClient.post('/auth/refresh');
    return true;
  } catch {
    return false;
  }
}

/** `signal` lets the app-shell bootstrap give up quickly instead of waiting on the client's 120s default. */
export async function getMe(options: { signal?: AbortSignal } = {}): Promise<User> {
  return apiClient.get<User>('/auth/me', options);
}

export async function getCsrfToken(options: { signal?: AbortSignal } = {}): Promise<string> {
  const response = await apiClient.get<{ csrfToken: string }>('/auth/csrf-token', options);
  apiClient.setCsrfToken(response.csrfToken);
  return response.csrfToken;
}
